import { useMemo } from "react";
import { Polygon, Tooltip } from "react-leaflet";
import type { LatLngTuple } from "leaflet";
import { useQuery } from "@tanstack/react-query";

import { telemetryAjax } from "../../ajax/TelemetryAjax";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { BlastPatternFenceDTO } from "../../DTOs/Telemetry/BlastPatternFenceDTO";
import { MINIMUM_VERTICES, formatAreaMetres, parseGeofencePolygon, polygonAreaAndPerimeterMetres } from "./geofencePolygon";

/**
 * Draws the LIVE blast patterns - the ones still being worked - beneath the fleet.
 *
 * Note: a different colour from the location fences on purpose. The two answer different questions and
 * routinely overlap, since a blast pattern sits inside a pit whose own fence is already drawn. In one
 * colour the pit outline and the patterns inside it would read as one tangled shape.
 *
 * Note: "live" is decided by the SERVER (Completed = false), not here. Finished blasts accumulate forever
 * - 993 patterns against 681 not completed on the test database - so filtering client-side would mean
 * shipping the whole history down the wire to throw most of it away.
 */

// Note: warmer than the location fence grey so the two layers separate at a glance, but still muted -
// these are context for reading the fleet, not the subject of the map.
const BLAST_PATTERN_COLOUR = "#B0651F";

// Note: a pattern with live, fenced dig blocks under it is sized to actually enclose them, which can make
// it many times the area of a pattern with none - a real difference in what the shape represents, not a
// sizing bug, and worth reading as one at a glance rather than looking like an inconsistency. Bolder line,
// no dash, so "this ground has confirmed activity" reads as deliberate rather than an odd outlier.
const CONFIRMED_PATTERN_STYLE = { weight: 3, opacity: 0.95, fillOpacity: 0.16 };
const UNCONFIRMED_PATTERN_STYLE = { weight: 1.5, opacity: 0.8, fillOpacity: 0.08, dashArray: "4 3" };

export const BLAST_PATTERN_FENCE_QUERY_KEY = ["telemetry", "blastPatternFences"];

type BlastPatternFenceLayerProps = {
  visible: boolean;
};

export const BlastPatternFenceLayer: React.FC<BlastPatternFenceLayerProps> = ({ visible }) => {
  const { data: fences = [] } = useQuery({
    queryKey: BLAST_PATTERN_FENCE_QUERY_KEY,
    queryFn: async () => {
      const res = await telemetryAjax.getLiveBlastPatternFences();
      // Note: this endpoint returns the array directly rather than a PagedResult, matching
      // telemetry/positions/current - so the error guard is the only unwrapping needed.
      if (ErrorResponse.isErrorResponse(res)) return [] as BlastPatternFenceDTO[];
      return res;
    },
    // Note: longer than the location fences' 5 minutes. A pattern's outline changes when someone draws it
    // or marks the blast complete, neither of which happens while a dispatcher watches the map.
    staleTime: 15 * 60 * 1000,
  });

  const shapes = useMemo(() => fences.flatMap((fence: BlastPatternFenceDTO) => {
    const { vertices } = parseGeofencePolygon(fence.geofencePolygon);

    // Note: a polygon that will not parse is skipped rather than drawn wrong. The server logs it by
    // pattern id when inference hits the same row, so it is diagnosable there rather than here.
    if (vertices.length < MINIMUM_VERTICES) return [];

    return [{
      id: fence.patternBlastId,
      reference: fence.reference,
      positions: vertices.map(v => [v.lat, v.lng] as LatLngTuple),
      measurements: polygonAreaAndPerimeterMetres(vertices),
      hasFencedDigBlocks: fence.hasFencedDigBlocks,
    }];
  }), [fences]);

  if (!visible) return null;

  return (
    <>
      {shapes.map(shape => (
        <Polygon
          key={`blast-${shape.id}`}
          positions={shape.positions}
          pathOptions={{
            color: BLAST_PATTERN_COLOUR,
            ...(shape.hasFencedDigBlocks ? CONFIRMED_PATTERN_STYLE : UNCONFIRMED_PATTERN_STYLE),
          }}
        >
          <Tooltip>
            {shape.reference} - blast pattern
            {shape.measurements && ` - ${formatAreaMetres(shape.measurements.areaSqM)}, ${Math.round(shape.measurements.perimeterM).toLocaleString()} m perimeter`}
            {shape.hasFencedDigBlocks && " - confirmed by live dig blocks"}
          </Tooltip>
        </Polygon>
      ))}
    </>
  );
};

export default BlastPatternFenceLayer;
